'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Music, Volume2 } from 'lucide-react';
import type { EditorAudioTrack } from '@/types/editor';
import { fallbackAudioWaveform, sampleAudioWaveform } from '@/lib/editor/audioWaveform';
import { snapTimeToFrame } from '@/lib/editor/frameGrid';

interface Props {
  audioTrack: EditorAudioTrack;
  maxDurationSeconds: number;
  selected: boolean;
  onSelect: (trackId: string) => void;
  onChange: (trackId: string, patch: Partial<EditorAudioTrack>) => void;
}

const waveformCache = new Map<string, number[]>();

function useAudioWaveform(audioTrack: EditorAudioTrack | null, barCount = 96) {
  const source = audioTrack?.src ?? audioTrack?.s3Key ?? audioTrack?.fileName ?? 'audio';
  const audioSrc = audioTrack?.src;
  const fallback = useMemo(() => fallbackAudioWaveform(source, barCount), [barCount, source]);
  const [peaks, setPeaks] = useState<number[]>(() => waveformCache.get(source) ?? fallback);

  useEffect(() => {
    const cached = waveformCache.get(source);
    if (cached) {
      setPeaks(cached);
      return;
    }
    if (!audioSrc || typeof window === 'undefined') {
      setPeaks(fallback);
      return;
    }

    let cancelled = false;
    let closed = false;
    const context = new AudioContext();
    const closeContext = () => {
      if (closed) return;
      closed = true;
      if (context.state === 'closed') return;
      void context.close().catch(() => {});
    };
    void fetch(audioSrc)
      .then((response) => {
        if (!response.ok) throw new Error(`waveform fetch failed: ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => context.decodeAudioData(buffer))
      .then((decoded) => {
        if (cancelled) return;
        const decodedPeaks = sampleAudioWaveform(decoded.getChannelData(0), barCount);
        waveformCache.set(source, decodedPeaks);
        setPeaks(decodedPeaks);
      })
      .catch(() => {
        if (!cancelled) setPeaks(fallback);
      })
      .finally(() => {
        closeContext();
      });

    return () => {
      cancelled = true;
      closeContext();
    };
  }, [audioSrc, barCount, fallback, source]);

  return peaks;
}

type AudioDragMode = 'move' | 'trim-left' | 'trim-right';

export function AudioTimelineRow({ audioTrack, maxDurationSeconds, selected, onSelect, onChange }: Props) {
  const peaks = useAudioWaveform(audioTrack);
  const dragRef = useRef<{
    mode: AudioDragMode;
    startX: number;
    start: number;
    duration: number;
    sourceOffset: number;
    laneWidth: number;
    moved: boolean;
  } | null>(null);
  const trackId = audioTrack.id;
  if (!trackId) return null;
  const startPercent = maxDurationSeconds > 0
    ? Math.min(100, (Math.max(0, audioTrack.start ?? 0) / maxDurationSeconds) * 100)
    : 0;
  const timelineDuration = audioTrack.timelineDuration ?? audioTrack.duration ?? maxDurationSeconds;
  const widthPercent =
    maxDurationSeconds > 0
      ? Math.min(100 - startPercent, (timelineDuration / maxDurationSeconds) * 100)
      : 0;

  const beginDrag = (mode: AudioDragMode) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const laneWidth = event.currentTarget.closest<HTMLElement>('[data-audio-lane]')?.clientWidth ?? 0;
    if (laneWidth <= 0) return;
    dragRef.current = {
      mode,
      startX: event.clientX,
      start: Math.max(0, audioTrack.start ?? 0),
      duration: Math.max(0.1, timelineDuration),
      sourceOffset: Math.max(0, audioTrack.sourceOffset ?? 0),
      laneWidth,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelect(trackId);
  };

  const moveDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = ((event.clientX - drag.startX) / drag.laneWidth) * maxDurationSeconds;
    if (!drag.moved && Math.abs(event.clientX - drag.startX) < 3) return;
    drag.moved = true;
    if (drag.mode === 'move') {
      const start = snapTimeToFrame(
        Math.min(Math.max(0, maxDurationSeconds - drag.duration), Math.max(0, drag.start + delta))
      );
      onChange(trackId, { start });
      return;
    }
    if (drag.mode === 'trim-left') {
      const boundedDelta = Math.min(
        drag.duration - 0.1,
        Math.max(-Math.min(drag.sourceOffset, drag.start), delta)
      );
      onChange(trackId, {
        start: snapTimeToFrame(Math.max(0, drag.start + boundedDelta)),
        timelineDuration: snapTimeToFrame(Math.max(0.1, drag.duration - boundedDelta)),
        sourceOffset: snapTimeToFrame(Math.max(0, drag.sourceOffset + boundedDelta)),
      });
      return;
    }
    onChange(trackId, {
      timelineDuration: snapTimeToFrame(
        Math.min(maxDurationSeconds - drag.start, Math.max(0.1, drag.duration + delta))
      ),
    });
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag?.moved) onSelect(trackId);
  };

  return (
    <div data-audio-lane className="relative h-full overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]">
      <button
        type="button"
        data-timeline-control
        onPointerDown={beginDrag('move')}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={`group absolute inset-y-1 flex touch-none items-center gap-1.5 rounded-md border bg-coral-500/20 px-2 transition ${
          selected ? 'border-coral-200 ring-1 ring-coral-300/70' : 'border-coral-400/30 hover:border-coral-300/60'
        }`}
        style={{ left: `${startPercent}%`, width: `${widthPercent}%` }}
      >
        <span
          onPointerDown={beginDrag('trim-left')}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="absolute inset-y-1 left-0.5 z-20 w-1 cursor-ew-resize rounded-full bg-white/70 opacity-0 transition group-hover:opacity-100"
        />
        <span aria-hidden className="pointer-events-none absolute inset-0 flex items-center gap-px overflow-hidden px-1.5 opacity-55">
          {peaks.map((peak, index) => (
            <span
              key={index}
              className="min-w-px flex-1 rounded-full bg-coral-200/70"
              style={{ height: `${Math.max(12, peak * 76)}%` }}
            />
          ))}
        </span>
        <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-coral-950/90 via-coral-950/35 to-coral-950/65" />
        <Music className="h-3 w-3 shrink-0 text-coral-300" />
        <span className="relative truncate text-[10px] font-black text-coral-100">
          {audioTrack.fileName ?? 'Audio'}
        </span>
        <span className="relative ml-auto flex shrink-0 items-center gap-1 text-[9px] font-bold tabular-nums text-coral-100/70">
          <Volume2 className="h-2.5 w-2.5" /> {Math.round(audioTrack.volume * 100)}%
          {audioTrack.muted ? ' · M' : ''}
          {audioTrack.solo ? ' · S' : ''}
          {audioTrack.ducking ? ' · DUCK' : ''}
        </span>
        <span
          onPointerDown={beginDrag('trim-right')}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className="absolute inset-y-1 right-0.5 z-20 w-1 cursor-ew-resize rounded-full bg-white/70 opacity-0 transition group-hover:opacity-100"
        />
      </button>
    </div>
  );
}
